import React, { useState, useMemo, useRef } from 'react'
import FindADealer from '../../../3_Organisms/FindADealer/FindADealer'
import generalController from '../../../../controllers/generalController'
import { languageController } from '../../../../controllers/language/languageController'
import Checkbox from '../../../1_Atoms/Forms/Checkbox/Checkbox'
import Form from '../../../2_Molecules/Form/Form'
import Button from '../../../1_Atoms/Button/Button'
import { useForm } from '../../../../hooks/useForm'
import parse from 'html-react-parser';
import { handleRequest } from '../../../../utils/httpRequest'
import { getEndpointUrl } from '../../../../utils/endPoints'
import pidController from '../../../../controllers/pidController'
import { pushToGTMDataLayer, scrollTo } from '../../../../utils/helpers'

interface CurrentVehicleData {
  tradeInId: number | string
  tradeInMake: string
  tradeInModel: string
  tradeInYear: number
  tradeInTrim: string
  tradeInPriceLow: number
  tradeInPriceHigh: number
  searchTerm: string
  tradeInOdometer: number | string
}

interface UserInformation {
  firstname: string
  lastname: string
  email: string
  phone: string
  preferredContact: string
  message: string
  purchaseDate: string
}

interface DesiredVehicleData {
  id: number | string
  year: number | string
  model: string
  msrp: number
  isEV?: boolean
}

interface DealerData {
  dealer_code?: string | number
  [key: string]: any
}

interface FindDealerStepProps {
  handleNextStep: (data: { dealerData: DealerData; isOptin: boolean }) => void
  currentVehicleData: CurrentVehicleData | null
  desiredVehicleData: DesiredVehicleData | null
  userInformation: UserInformation | null
  source: string
  language: string
  formDisclaimer: string
  preDefinedUserLocation?: { lat: number; lng: number } | null
  preDefinedDealerData?: DealerData | null
}

interface FormData {
  optin: boolean
}

const FindDealerStep: React.FC<FindDealerStepProps> = ({
  handleNextStep,
  currentVehicleData,
  desiredVehicleData,
  userInformation,
  source,
  language,
  formDisclaimer,
  preDefinedUserLocation,
  preDefinedDealerData,
}) => {
  const [dealerData, setDealerData] = useState<DealerData | undefined>(preDefinedDealerData || undefined)
  const [formError, setFormError] = useState<string | null>(null)
  const formRef = useRef<HTMLDivElement>(null)

  const urlParams = generalController.getURLSearchParams();
  const pidValue = urlParams['bnp'] === '1' ? 'tradeInValueBnp' : 'tradeInValue';

  const { handleSubmit, data, errors, setData } = useForm({
    initState: {
      optin: false,
    },
    // Send the form data if validated
    onSubmit: async () => {
      try {
        const submissionData: Record<string, any> = {
          dealerId: Number(dealerData?.dealer_code),
          OptIn: optin,
          pid: pidController.getPid(pidValue),
          source: source,
          language: language,
        }

        // Add currentVehicleData data if passed
        if (currentVehicleData) {
          submissionData.TradeIn_Id = Number(currentVehicleData.tradeInId);
          submissionData.TradeIn_Year = Number(currentVehicleData.tradeInYear);
          submissionData.TradeIn_Make = currentVehicleData.tradeInMake;
          submissionData.TradeIn_Model = currentVehicleData.tradeInModel;
          submissionData.TradeIn_Trim = currentVehicleData.tradeInTrim;
          submissionData.TradeIn_odometer = Number(currentVehicleData.tradeInOdometer);
          submissionData.TradeIn_PriceLow = currentVehicleData.tradeInPriceLow;
          submissionData.TradeIn_PriceHigh = currentVehicleData.tradeInPriceHigh;
          submissionData.vehicleSearch = currentVehicleData.searchTerm;
        }

        // Add userInformation data if passed
        if (userInformation) {
          submissionData.firstname = userInformation.firstname;
          submissionData.lastname = userInformation.lastname;
          submissionData.email = userInformation.email;
          submissionData.phone = userInformation.phone;
          submissionData.preferredContact = userInformation.preferredContact;
          submissionData.PurchaseTimeFrame = userInformation.purchaseDate;
          submissionData.message = userInformation.message;
        }

        // Add desiredVehicleData data if passed
        if (desiredVehicleData) {
          submissionData.Buy_Id = desiredVehicleData.id;
          submissionData.Buy_Year = desiredVehicleData.year;
          submissionData.Buy_Make = 'Mazda'; // Always set as 'Mazda'
          submissionData.Buy_Model = desiredVehicleData.model;
          submissionData.Buy_Trim = undefined; // Always blank or not defined
          submissionData.Buy_Price = desiredVehicleData.msrp;
        }

        const res = await handleRequest('post', getEndpointUrl('cbb') ?? '', submissionData);

        if (res.Success) {
          pushToGTMDataLayer({ formevent: 'submit' });
          handleNextStep({ dealerData: dealerData!, isOptin: optinBoolean })
        } else {
          setFormError(res.state ? res.state : res.Errors[0])
        }
      } catch (err) {
        console.log(err)
      }
    },
  })

  const { optin } = data;

  const optinBoolean = Boolean(optin);

  // Only render FindADealer when a different vahicle is selected in step 2.
  const MemoFindADealer = useMemo(() => {
    return (
      <FindADealer
        isStepRenderType={true}
        onSelectDealer={(data: any) => {
          console.log('hallow', data)
          setDealerData(data.dealer);
          // Scroll to the bottom form upon dealer selection.
          if (formRef.current) {
            scrollTo({ y: formRef.current, offsetY: 100 });
          }
        }}
        isEVonly={desiredVehicleData?.isEV ? true : false}
        preDefinedUserLocation={preDefinedUserLocation || undefined}
        preDefinedDealerId={preDefinedDealerData?.dealer_code ? String(preDefinedDealerData.dealer_code) : undefined}
        formExtraParams={{}}
      />
    );
  }, [desiredVehicleData]);

  console.log(dealerData)
  return (
    <div className="mz-find-dealer-step-tie">
      <div className="mz-find-dealer-step-tie__dealer">
        <h5 className="mz-find-dealer-step-tie__title">{languageController.getCopy('tradeInEstimator', 'step4Title')}</h5>
        {MemoFindADealer}
      </div>
      <div className="mz-find-dealer-step-tie__form mz-container" ref={formRef}>
        <div className="grid-inner-content">
          <Form
            onSubmit={handleSubmit}
            isDark={false}
            noValidate={true}
            errorMessage={formError ?? undefined}
          >
            <div className="mz-find-dealer-step-tie__email-encouragement label label--tight">
              {languageController.getCopy('global', 'emailEncouragement')}
            </div>
            <Checkbox
              name="optin"
              checked={optinBoolean}
              onChange={(e) => setData({ optin: !optin })}
              label={formDisclaimer ? parse(formDisclaimer) : ''}
            />
            <Button
              type="submit"
              className="mz-btn--primary mz-btn--red"
              label={languageController.getCopy('tradeInEstimator', 'step4Submit')}
              disabled={(dealerData === undefined)}
            />
          </Form>
        </div>
      </div>
    </div>
  )
}

export default FindDealerStep
